import { accountStore } from '../editor/storage';

/*
 * Clips and music of video projects, kept ONLY on this device (IndexedDB), per account, next to the autosave
 * (src/editor/storage.ts). A project's JSON names them by key (src/video/project.ts `media`).
 *
 * Browsers may clear a site's stored data (Safari: a site not opened for a while, unless it was added to the
 * home screen), so every reader must cope with a key that is gone: hasMedia() tells which ones are missing and
 * the video screen says so (src/ui/videoView.ts) instead of showing an empty picture.
 */

const STORE = 'media';

export interface MediaInfo {
  kind: 'clip' | 'music' | 'photo';
  /** original file name */
  name: string;
  type: string;
  bytes: number;
  /** seconds */
  duration: number;
  /** small picture of the clip (data URL), for the timeline */
  thumb?: string;
  /** music from the group's library (0009): its id, so the same song is not downloaded twice */
  library?: string;
  savedAt: number;
}
interface Row extends MediaInfo { blob: Blob }

let opened: { name: string; db: Promise<IDBDatabase> } | null = null;

function open(): Promise<IDBDatabase> {
  const base = accountStore();
  if (!base) return Promise.reject(new Error('ยังไม่รู้ว่าเป็นบัญชีไหน จึงยังเก็บคลิปในเครื่องไม่ได้'));
  if (!('indexedDB' in window)) return Promise.reject(new Error('เบราว์เซอร์นี้ไม่มีที่เก็บข้อมูลในเครื่อง จึงเก็บคลิปไม่ได้'));
  const name = `${base}-media`;
  if (opened?.name === name) return opened.db;
  const db = new Promise<IDBDatabase>((res, rej) => {
    const r = indexedDB.open(name, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE);
    r.onsuccess = () => {
      const db = r.result;
      // the browser may close the connection (storage cleared, another tab upgrading): open again next time
      db.onclose = () => { if (opened?.name === name) opened = null; };
      db.onversionchange = () => { db.close(); if (opened?.name === name) opened = null; };
      res(db);
    };
    r.onerror = () => rej(r.error ?? new Error('เปิดที่เก็บคลิปในเครื่องไม่ได้'));
  });
  opened = { name, db };
  db.catch(() => { if (opened?.db === db) opened = null; });
  return db;
}

function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  return open().then(db => new Promise<T | undefined>((res, rej) => {
    const t = db.transaction(STORE, mode);
    const req = fn(t.objectStore(STORE));
    t.oncomplete = () => res(req ? req.result : undefined);
    t.onerror = () => rej(t.error ?? new Error('อ่านหรือเขียนที่เก็บคลิปไม่สำเร็จ'));
    t.onabort = () => rej(t.error ?? new Error('ยกเลิกการเขียนที่เก็บคลิป'));
  }));
}

/** A readable Thai reason for a failed media save. */
export function mediaErrorText(e: unknown): string {
  if (e instanceof DOMException && e.name === 'QuotaExceededError') {
    return 'พื้นที่เก็บข้อมูลของเว็บในเครื่องนี้เต็ม เลือกช่วงคลิปให้สั้นลง หรือลบแอปและไฟล์อื่นในเครื่องให้มีที่ว่างมากขึ้น';
  }
  return e instanceof Error && e.message ? e.message : 'ไม่ทราบสาเหตุ';
}

export async function putMedia(key: string, blob: Blob, info: Omit<MediaInfo, 'bytes' | 'type' | 'savedAt'>): Promise<void> {
  const row: Row = { ...info, blob, type: blob.type, bytes: blob.size, savedAt: Date.now() };
  await tx('readwrite', s => { s.put(row, key); });
}

export async function getMedia(key: string): Promise<(MediaInfo & { blob: Blob }) | null> {
  const row = await tx<Row>('readonly', s => s.get(key));
  return row ?? null;
}

export async function deleteMedia(key: string): Promise<void> {
  await tx('readwrite', s => { s.delete(key); });
}

/** which of these keys are stored on this device */
export async function hasMedia(keys: string[]): Promise<Set<string>> {
  const found = new Set<string>();
  if (!keys.length) return found;
  const all = await tx<IDBValidKey[]>('readonly', s => s.getAllKeys());
  const have = new Set((all ?? []).map(String));
  for (const k of keys) if (have.has(k)) found.add(k);
  return found;
}

/** every stored file's key and size (the files themselves are not read) */
export async function listMedia(): Promise<Array<{ key: string; bytes: number; kind: MediaInfo['kind']; library?: string }>> {
  const out: Array<{ key: string; bytes: number; kind: MediaInfo['kind']; library?: string }> = [];
  await open().then(db => new Promise<void>((res, rej) => {
    const t = db.transaction(STORE, 'readonly');
    const req = t.objectStore(STORE).openCursor();
    req.onsuccess = () => {
      const c = req.result;
      if (!c) return;
      const v = c.value as Row;
      out.push({ key: String(c.key), bytes: v.bytes || 0, kind: v.kind, library: v.library });
      c.continue();
    };
    t.oncomplete = () => res();
    t.onerror = () => rej(t.error ?? new Error('อ่านรายการคลิปในเครื่องไม่สำเร็จ'));
  }));
  return out;
}

/** total size of the clips and music on this device (bytes) */
export async function mediaBytes(): Promise<number> {
  const rows = await tx<Row[]>('readonly', s => s.getAll());
  return (rows ?? []).reduce((n, r) => n + (r.bytes || 0), 0);
}

/**
 * Ask the browser to keep this site's data (so clips are not cleared when space runs low).
 * Returns whether it is kept; never throws.
 */
export async function keepStorage(): Promise<boolean> {
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted?.()) return true;
    return await navigator.storage.persist();
  } catch { return false; }
}

/** free space the browser still gives this site (bytes), or null when it does not say */
export async function spaceLeft(): Promise<number | null> {
  try {
    const e = await navigator.storage?.estimate?.();
    if (!e || e.quota === undefined || e.usage === undefined) return null;
    return Math.max(0, e.quota - e.usage);
  } catch { return null; }
}

/** blob: URLs of stored media for playing; revoke() frees them all */
export function createUrlCache() {
  const urls = new Map<string, string>();
  const waiting = new Map<string, Promise<string | null>>();
  return {
    async url(key: string): Promise<string | null> {
      const have = urls.get(key);
      if (have) return have;
      // two callers at once share one read (otherwise each makes its own address and one is never freed)
      let p = waiting.get(key);
      if (!p) {
        p = getMedia(key).then(m => {
          if (!m) return null;
          const u = URL.createObjectURL(m.blob);
          urls.set(key, u);
          return u;
        }).finally(() => waiting.delete(key));
        waiting.set(key, p);
      }
      return p;
    },
    forget(key: string) { const u = urls.get(key); if (u) { URL.revokeObjectURL(u); urls.delete(key); } },
    revoke() { for (const u of urls.values()) URL.revokeObjectURL(u); urls.clear(); }
  };
}
